import { useCallback, useEffect, useMemo, useState } from 'react';
import paidAmountSettlementService from '../services/paidAmountSettlementService.js';
import {
  mlCreationHardBlocked,
  mlCreationHasOutstanding,
} from '../utils/mlPaidSettlementRules.js';

const EMPTY_BLOCK = { blocked: false, has_outstanding: false };

export function useMlCreationPaidSettlement(user) {
  const role = user?.role;
  const isAgent = role === 'Agent';
  const isHubAdmin = role === 'Hub Admin';
  // Unremitted paid collections are an agent concern only.
  // Hub Admin dispatches incoming ITCL / ML sheets without this gate.
  const applies = Boolean(isAgent);

  const [loaded, setLoaded] = useState(!applies);
  const [mlCreationBlock, setMlCreationBlock] = useState(EMPTY_BLOCK);
  const [dues, setDues] = useState(null);
  const [agentDues, setAgentDues] = useState([]);
  const [settleOpen, setSettleOpen] = useState(false);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!applies) {
      setMlCreationBlock(EMPTY_BLOCK);
      setDues(null);
      setAgentDues([]);
      setLoaded(true);
      return;
    }

    try {
      const blockRes = await paidAmountSettlementService.getMlCreationBlock();
      const block = blockRes?.data || EMPTY_BLOCK;
      setMlCreationBlock(block);
      setAgentDues([]);

      if (mlCreationHasOutstanding(block) || mlCreationHardBlocked(block)) {
        const duesRes = await paidAmountSettlementService.getOpenDues();
        setDues(duesRes?.data?.items?.[0] || null);
      } else {
        setDues(null);
      }
    } catch {
      if (!silent) {
        setMlCreationBlock(EMPTY_BLOCK);
        setDues(null);
        setAgentDues([]);
      }
    } finally {
      setLoaded(true);
    }
  }, [applies]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const ocrPending = mlCreationBlock.pending_request_status === 'OCR_PENDING';
  useEffect(() => {
    if (!ocrPending) return undefined;
    const timer = window.setInterval(() => refresh({ silent: true }), 5000);
    return () => window.clearInterval(timer);
  }, [ocrPending, refresh]);

  const settlementPath = useMemo(() => {
    if (mlCreationBlock.settlement_path) return mlCreationBlock.settlement_path;
    return '/paid-amount-settlements';
  }, [mlCreationBlock.settlement_path]);

  return {
    applies,
    isHubAdmin,
    loaded,
    mlCreationBlock,
    dues,
    agentDues,
    hardBlocked: mlCreationHardBlocked(mlCreationBlock),
    hasOutstanding: mlCreationHasOutstanding(mlCreationBlock),
    showGate: mlCreationHardBlocked(mlCreationBlock) || mlCreationHasOutstanding(mlCreationBlock),
    settlementPath,
    settleOpen,
    setSettleOpen,
    openSettle: () => {
      if (!isAgent) return false;
      if (dues?.pending_request_id) return false;
      setSettleOpen(true);
      return true;
    },
    refresh,
  };
}

export default useMlCreationPaidSettlement;
